// Flattens a background image + an optional overlay image into a single
// canvas-exported PNG blob. Used by the "highlight" row builder to turn a
// background + a searched tile's image into one real image file, so
// everything downstream (Page export, TV app) just sees a normal image URL.

export const ALIGNMENT_OPTIONS = ["top-left", "top-right", "center", "bottom-left", "bottom-right"];

// Fetch the image ourselves and load it from a local blob: URL instead of
// setting crossOrigin="anonymous" directly on an <img src="remote-url">.
// The same remote URL is also displayed elsewhere on the page as a plain
// (non-CORS) <img> — Chrome can inconsistently serve that cached, non-CORS
// response back to a crossOrigin="anonymous" request, silently tainting the
// canvas on the 2nd+ load of the same URL (so only the first regenerate ever
// succeeds). A blob: URL is same-origin by construction, so this can't happen.
const loadImage = async (url) => {
  const response = await fetch(url, { mode: "cors" });
  if (!response.ok) throw new Error(`Failed to load image: ${url}`);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Failed to decode image: ${url}`));
    };
    img.src = objectUrl;
  });
};

// Many base/template images are designed with a solid black rectangle as a
// literal cutout for the overlay. Finds the LARGEST contiguous black region
// (flood fill on a downsampled grid, not just a global min/max bounding box)
// so a few stray dark pixels elsewhere in the image (shadows, outlines on the
// number graphics, etc.) can't balloon the detected area beyond the real spot.
const detectBlackRegion = (ctx, width, height, threshold = 12) => {
  const { data } = ctx.getImageData(0, 0, width, height);
  const step = Math.max(1, Math.floor(Math.min(width, height) / 300));
  const cols = Math.ceil(width / step);
  const rows = Math.ceil(height / step);
  const isBlack = new Uint8Array(cols * rows);

  for (let ry = 0; ry < rows; ry++) {
    const y = Math.min(ry * step, height - 1);
    for (let rx = 0; rx < cols; rx++) {
      const x = Math.min(rx * step, width - 1);
      const i = (y * width + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      if (a > 200 && r <= threshold && g <= threshold && b <= threshold) {
        isBlack[ry * cols + rx] = 1;
      }
    }
  }

  const visited = new Uint8Array(cols * rows);
  let best = null;
  const stack = [];

  for (let ry = 0; ry < rows; ry++) {
    for (let rx = 0; rx < cols; rx++) {
      const startIdx = ry * cols + rx;
      if (!isBlack[startIdx] || visited[startIdx]) continue;

      let minRx = rx;
      let maxRx = rx;
      let minRy = ry;
      let maxRy = ry;
      let count = 0;

      stack.length = 0;
      stack.push(startIdx);
      visited[startIdx] = 1;

      while (stack.length) {
        const cur = stack.pop();
        const cy = Math.floor(cur / cols);
        const cx = cur % cols;
        count++;
        if (cx < minRx) minRx = cx;
        if (cx > maxRx) maxRx = cx;
        if (cy < minRy) minRy = cy;
        if (cy > maxRy) maxRy = cy;

        const neighbors = [
          [cx - 1, cy],
          [cx + 1, cy],
          [cx, cy - 1],
          [cx, cy + 1],
        ];
        for (const [nx, ny] of neighbors) {
          if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
          const nIdx = ny * cols + nx;
          if (isBlack[nIdx] && !visited[nIdx]) {
            visited[nIdx] = 1;
            stack.push(nIdx);
          }
        }
      }

      if (!best || count > best.count) {
        best = { minRx, maxRx, minRy, maxRy, count };
      }
    }
  }

  if (!best) return null;

  const rawX = best.minRx * step;
  const rawY = best.minRy * step;
  const rawWidth = (best.maxRx - best.minRx + 1) * step;
  const rawHeight = (best.maxRy - best.minRy + 1) * step;

  // Pad by one sampling step on every side — the grid is downsampled for
  // performance, and anti-aliased edges on the template can fall just
  // outside the strict black threshold, both of which can under-detect the
  // true cutout boundary by a few pixels and leave a sliver exposed.
  const x = Math.max(0, rawX - step);
  const y = Math.max(0, rawY - step);
  const right = Math.min(width, rawX + rawWidth + step);
  const bottom = Math.min(height, rawY + rawHeight + step);

  return { x, y, width: right - x, height: bottom - y };
};

// Font choices offered in the label editor — common system/web-safe fonts
// only, so text always renders identically without waiting on an async
// webfont load (no FontFace/@font-face involved).
export const LABEL_FONT_FAMILIES = [
  "Arial",
  "Helvetica",
  "Verdana",
  "Tahoma",
  "Trebuchet MS",
  "Georgia",
  "Times New Roman",
  "Courier New",
  "Impact",
  "Comic Sans MS",
];

export const LABEL_FONT_WEIGHTS = [
  { value: "400", label: "Normal" },
  { value: "500", label: "Medium" },
  { value: "600", label: "Semibold" },
  { value: "700", label: "Bold" },
  { value: "800", label: "Extra Bold" },
  { value: "900", label: "Black" },
];

// Draws a fully customizable text label (e.g. "NEW RELEASE") anywhere on the
// canvas. Supports plain or gradient fill independently for the text and its
// optional rounded-pill background, any font family/size/weight/style, and
// free x/y placement (a fraction of canvas size, not a fixed corner/center
// slot) so it can be dragged to literally anywhere on the image.
const drawLabel = (ctx, canvasWidth, canvasHeight, label) => {
  if (!label || !label.text || !label.text.trim()) return;

  const {
    text,
    xPct = 0.05,
    yPct = 0.05,
    fontFamily = "Arial",
    fontSize,
    fontWeight = "700",
    fontStyle = "normal",
    showBackground = true,
    textColorType = "solid",
    textColor = "#ffffff",
    textGradientFrom = "#ffffff",
    textGradientTo = "#cccccc",
    bgColorType = "solid",
    bgColor = "#dc2626",
    bgGradientFrom = "#ec4899",
    bgGradientTo = "#dc2626",
  } = label;

  const size = fontSize || Math.max(12, Math.round(canvasWidth * 0.035));
  const italic = fontStyle === "italic" ? "italic " : "";
  ctx.font = `${italic}${fontWeight} ${size}px "${fontFamily}", Arial, sans-serif`;

  const paddingX = showBackground ? size * 0.7 : 0;
  const paddingY = showBackground ? size * 0.4 : 0;
  const textWidth = ctx.measureText(text).width;
  const boxWidth = textWidth + paddingX * 2;
  const boxHeight = size + paddingY * 2;

  // xPct/yPct is the top-left corner of the label box, as a fraction of the
  // canvas — free placement anywhere the admin drags it, not limited to a
  // handful of fixed corner/center slots. Clamped so the box can never end
  // up mostly or fully off-canvas.
  const x = Math.min(Math.max(xPct * canvasWidth, 0), Math.max(0, canvasWidth - boxWidth));
  const y = Math.min(Math.max(yPct * canvasHeight, 0), Math.max(0, canvasHeight - boxHeight));

  if (showBackground) {
    let bgFill = bgColor;
    if (bgColorType === "gradient") {
      const grad = ctx.createLinearGradient(x, y, x + boxWidth, y + boxHeight);
      grad.addColorStop(0, bgGradientFrom);
      grad.addColorStop(1, bgGradientTo);
      bgFill = grad;
    }
    const radius = boxHeight / 4;
    ctx.fillStyle = bgFill;
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + boxWidth, y, x + boxWidth, y + boxHeight, radius);
    ctx.arcTo(x + boxWidth, y + boxHeight, x, y + boxHeight, radius);
    ctx.arcTo(x, y + boxHeight, x, y, radius);
    ctx.arcTo(x, y, x + boxWidth, y, radius);
    ctx.closePath();
    ctx.fill();
  }

  let textFill = textColor;
  if (textColorType === "gradient") {
    const grad = ctx.createLinearGradient(x, y, x + boxWidth, y + boxHeight);
    grad.addColorStop(0, textGradientFrom);
    grad.addColorStop(1, textGradientTo);
    textFill = grad;
  }
  ctx.fillStyle = textFill;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  ctx.fillText(text, x + paddingX, y + boxHeight / 2);
};

// Draws `img` to completely fill a dWidth x dHeight destination rectangle —
// like CSS `object-fit: cover` — by cropping a matching-aspect-ratio slice
// out of the SOURCE image rather than scaling/positioning the whole thing.
// This guarantees the destination rectangle is fully covered with no gaps,
// while never drawing outside its bounds (the crop happens on the source
// side, so there's nothing left over to spill out).
const drawCover = (ctx, img, dx, dy, dWidth, dHeight) => {
  const imgAspect = img.naturalWidth / img.naturalHeight;
  const destAspect = dWidth / dHeight;
  let sx;
  let sy;
  let sWidth;
  let sHeight;

  if (imgAspect > destAspect) {
    sHeight = img.naturalHeight;
    sWidth = sHeight * destAspect;
    sx = (img.naturalWidth - sWidth) / 2;
    sy = 0;
  } else {
    sWidth = img.naturalWidth;
    sHeight = sWidth / destAspect;
    sx = 0;
    sy = (img.naturalHeight - sHeight) / 2;
  }

  ctx.drawImage(img, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);
};

const ALIGNMENT_ANCHORS = {
  "top-left": { h: 0, v: 0 },
  "top-right": { h: 1, v: 0 },
  center: { h: 0.5, v: 0.5 },
  "bottom-left": { h: 0, v: 1 },
  "bottom-right": { h: 1, v: 1 },
};

// Like `drawCover`, but the crop is anchored per `alignment` instead of
// always centered — e.g. "top-left" keeps the top-left portion of the
// source image and crops away the right/bottom, rather than cropping evenly
// from both sides. This is how `alignment` stays meaningful for a cutout
// that must be completely covered with no gaps (CSS calls the equivalent
// `object-position`, paired with `object-fit: cover`).
const drawCoverAligned = (ctx, img, dx, dy, dWidth, dHeight, alignment = "center") => {
  const imgAspect = img.naturalWidth / img.naturalHeight;
  const destAspect = dWidth / dHeight;
  let sWidth;
  let sHeight;

  if (imgAspect > destAspect) {
    sHeight = img.naturalHeight;
    sWidth = sHeight * destAspect;
  } else {
    sWidth = img.naturalWidth;
    sHeight = sWidth / destAspect;
  }

  const anchor = ALIGNMENT_ANCHORS[alignment] || ALIGNMENT_ANCHORS.center;
  const sx = (img.naturalWidth - sWidth) * anchor.h;
  const sy = (img.naturalHeight - sHeight) * anchor.v;

  ctx.drawImage(img, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);
};

export const generateCompositeImage = async ({
  backgroundUrl,
  overlayUrl,
  alignment = "center",
  overlayXPct,
  overlayYPct,
  overlayWidthPct,
  overlayHeightPct,
  targetWidth,
  targetHeight,
  label,
  // Skips the black-cutout scan entirely and always uses free drag/resize
  // placement. The scan is tuned for purpose-built templates that have one
  // real black placeholder rectangle (the Highlight row builder); on an
  // ordinary photo (e.g. a movie poster/backdrop with a dark scene or
  // letterboxing) it can false-positive, silently switching to "auto-fill
  // the detected region" and making a dragged overlay position look like
  // it's being ignored.
  disableCutoutDetection = false,
}) => {
  const background = await loadImage(backgroundUrl);
  const canvas = document.createElement("canvas");
  canvas.width = targetWidth || background.naturalWidth;
  canvas.height = targetHeight || background.naturalHeight;
  const ctx = canvas.getContext("2d");

  if (targetWidth && targetHeight) {
    drawCover(ctx, background, 0, 0, canvas.width, canvas.height);
  } else {
    ctx.drawImage(background, 0, 0, canvas.width, canvas.height);
  }

  // Detected once per background, regardless of whether an overlay has been
  // picked yet, so the UI can know right away whether THIS background has a
  // designed cutout (overlay auto-fills it, no manual positioning) or is a
  // plain image (admin drags/resizes the overlay freely) — not only after an
  // overlay is selected.
  const blackRegion = disableCutoutDetection ? null : detectBlackRegion(ctx, canvas.width, canvas.height);
  const hasCutout =
    !disableCutoutDetection &&
    Boolean(blackRegion) && blackRegion.width > canvas.width * 0.05 && blackRegion.height > canvas.height * 0.05;

  if (overlayUrl) {
    const overlay = await loadImage(overlayUrl);

    if (hasCutout) {
      // The base image has a literal cutout for the overlay — completely
      // fill it, cropping the overlay's own aspect ratio to match, so it's
      // aligned exactly onto the designed spot with no black edges ever
      // exposed. `alignment` chooses which part of the overlay's source
      // image survives that crop (like CSS object-position), so it still
      // has a real, visible effect on a fully-covered cutout.
      drawCoverAligned(ctx, overlay, blackRegion.x, blackRegion.y, blackRegion.width, blackRegion.height, alignment);
    } else {
      // A plain background with no designed cutout — the admin drags and
      // resizes the overlay freely on the preview instead, so just draw it
      // at whatever rectangle was set. Falls back to a sensible
      // aspect-preserved, centered default the first time, before the admin
      // has touched it.
      const overlayAspect = overlay.naturalWidth / overlay.naturalHeight;
      const defaultWidthPct = 0.3;
      const widthPct = typeof overlayWidthPct === "number" ? overlayWidthPct : defaultWidthPct;
      const heightPct =
        typeof overlayHeightPct === "number"
          ? overlayHeightPct
          : ((widthPct * canvas.width) / overlayAspect) / canvas.height;
      const xPct = typeof overlayXPct === "number" ? overlayXPct : (1 - widthPct) / 2;
      const yPct = typeof overlayYPct === "number" ? overlayYPct : (1 - heightPct) / 2;

      ctx.drawImage(
        overlay,
        xPct * canvas.width,
        yPct * canvas.height,
        widthPct * canvas.width,
        heightPct * canvas.height
      );
    }
  }

  // Text label (e.g. "NEW RELEASE") drawn last, on top of everything else,
  // so it's never hidden behind the background or overlay.
  drawLabel(ctx, canvas.width, canvas.height, label);

  // WebP instead of PNG — same visual quality for photographic content at a
  // fraction of the file size, so Save doesn't have to upload multi-MB
  // lossless PNGs per slot.
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/webp", 0.92));
  return { blob, width: canvas.width, height: canvas.height, hasCutout };
};
